import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../redux/helpers/axios";

/**
 * @author
 * @function ElevenProducts
 **/

const STORE_LABELS = { A1688: "1688" };

const formatPrice = (price) =>
  new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    maximumFractionDigits: 2,
  }).format(Number(price) || 0);

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

// 1 … 4 5 [6] 7 8 … 20
const pageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= total - 2)
    [total - 1, total - 2, total - 3].forEach((p) => pages.add(p));
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1] > 1) out.push("…");
    out.push(p);
  });
  return out;
};

const ProductImage = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken)
    return (
      <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
        No image
      </div>
    );
  return (
    <img
      referrerPolicy="no-referrer"
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setBroken(true)}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
    />
  );
};

export const ElevenProducts = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [featuring, setFeaturing] = useState({}); // { [productId]: true } while saving
  const [notice, setNotice] = useState(null); // { type, msg }

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(t);
  }, [notice]);

  // Optimistic: flip immediately, roll back if the request fails
  const toggleFeatured = async (product) => {
    if (featuring[product.id]) return;
    const next = !product.isFeatured;
    const setFlag = (value) =>
      setProducts((list) =>
        list.map((p) =>
          p.id === product.id ? { ...p, isFeatured: value } : p,
        ),
      );
    setFlag(next);
    setFeaturing((f) => ({ ...f, [product.id]: true }));
    try {
      const { data } = await axiosInstance.patch(
        `/eleven/admin/products/${product.id}/featured`,
        { isFeatured: next },
      );
      setNotice({ type: "success", msg: data.msg });
    } catch (err) {
      setFlag(!next);
      setNotice({
        type: "error",
        msg: err.response?.data?.msg || "Failed to update featured status",
      });
    } finally {
      setFeaturing((f) => {
        const copy = { ...f };
        delete copy[product.id];
        return copy;
      });
    }
  };

  const fetchProducts = useCallback(async (page) => {
    try {
      setLoading(true);
      setError("");

      // axios throws on non-2xx, so only `success` needs checking here
      const { data } = await axiosInstance.get(
        `/eleven/products/get-all?page=${page}`,
      );

      if (!data?.success) {
        throw new Error(data?.msg || "Failed to fetch products");
      }

      setProducts(data.products || []);
      setPagination(data.pagination || null);
    } catch (err) {
      console.error("Error fetching Eleven products:", err);
      setError(
        err.response?.data?.msg || err.message || "Something went wrong",
      );
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(currentPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, fetchProducts]);

  const goTo = (page) => {
    if (loading || !pagination) return;
    if (page < 1 || page > pagination.totalPages || page === currentPage)
      return;
    setCurrentPage(page);
  };

  const showingFrom = pagination
    ? (pagination.currentPage - 1) * pagination.pageSize + 1
    : 0;
  const showingTo = pagination
    ? Math.min(
        pagination.currentPage * pagination.pageSize,
        pagination.totalProducts,
      )
    : 0;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] content-start">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Products</h1>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fetchProducts(currentPage)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/eleven/products/new")}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1.5 whitespace-nowrap"
          >
            <span className="text-lg leading-none">+</span>
            Add Product
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs text-gray-500">Total Products</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {pagination?.totalProducts ?? "—"}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs text-gray-500">Featured (this page)</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {loading ? "—" : products.filter((p) => p.isFeatured).length}
          </p>
        </div>
        <div className="hidden sm:block bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs text-gray-500">Page</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">
            {pagination
              ? `${pagination.currentPage} / ${pagination.totalPages || 1}`
              : "—"}
          </p>
        </div>
      </div>

      {notice && (
        <div className="fixed z-[70] top-4 right-4 max-w-sm">
          <div
            className={`rounded-lg px-4 py-3 shadow-lg text-sm text-white ${
              notice.type === "error" ? "bg-red-600" : "bg-gray-900"
            }`}
          >
            {notice.msg}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm bg-red-50 border-red-200 text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => fetchProducts(currentPage)}
            className="font-semibold underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse"
            >
              <div className="bg-gray-200 aspect-square" />
              <div className="p-3 space-y-2">
                <div className="h-3 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-5 bg-gray-200 rounded w-1/2 mt-3" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {products.map((product) => {
            const variationCount =
              product._count?.variations ?? product.variations?.length ?? 0;
            const galleryCount = product.galleryImgs?.length || 0;

            return (
              <div
                key={product.id}
                className="group bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col hover:shadow-md hover:border-indigo-200 transition"
              >
                {/* Image */}
                <div className="relative aspect-square bg-gray-100 overflow-hidden">
                  <ProductImage
                    src={product.productImg}
                    alt={product.productTitle}
                  />

                  <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
                    {product.isFeatured && (
                      <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        Featured
                      </span>
                    )}
                  </div>

                  <span className="absolute top-2 right-2 text-[10px] font-medium bg-white/90 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-full">
                    {STORE_LABELS[product.store] || product.store}
                  </span>

                  {galleryCount > 0 && (
                    <span className="absolute bottom-2 right-2 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
                      {galleryCount} img
                    </span>
                  )}
                </div>

                {/* Details */}
                <div className="p-3 flex flex-col flex-1">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 truncate">
                    {product.elevenCategory?.name || "Uncategorized"}
                  </p>

                  <h3
                    className="text-sm font-medium text-gray-800 line-clamp-2 min-h-10 mt-0.5"
                    title={product.productTitle}
                  >
                    {product.productTitle}
                  </h3>

                  <div className="flex items-center justify-between mt-2">
                    <p className="text-indigo-600 font-bold">
                      {formatPrice(product.price)}
                    </p>
                    {variationCount > 0 && (
                      <span className="text-[10px] font-medium bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">
                        {variationCount} variant{variationCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
                    <span className="truncate" title={product.pid}>
                      PID: {product.pid}
                    </span>
                    <span className="shrink-0 ml-2">
                      {formatDate(product.createdAt)}
                    </span>
                  </div>

                  <div className="mt-auto pt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => toggleFeatured(product)}
                      disabled={Boolean(featuring[product.id])}
                      aria-pressed={product.isFeatured}
                      title={
                        product.isFeatured
                          ? "Remove from featured"
                          : "Mark as featured"
                      }
                      className={`inline-flex items-center justify-center gap-1 whitespace-nowrap px-2 py-2 text-xs font-semibold rounded-lg border transition disabled:opacity-60 ${
                        product.isFeatured
                          ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span aria-hidden="true">
                        {product.isFeatured ? "★" : "☆"}
                      </span>
                      {featuring[product.id]
                        ? "Saving…"
                        : product.isFeatured
                          ? "Unfeature"
                          : "Feature"}
                    </button>
                    <a
                      href={product.productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open supplier page"
                      className="inline-flex items-center justify-center whitespace-nowrap px-2 py-2 text-xs font-semibold rounded-lg border border-indigo-600 text-indigo-600 bg-white hover:bg-indigo-50 transition"
                    >
                      Source ↗
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !error && (
          <div className="bg-white rounded-lg border border-dashed border-gray-300 text-center py-16">
            <h3 className="text-lg font-semibold text-gray-700">
              No products found
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {currentPage > 1
                ? "There are no products on this page."
                : "Start by adding your first product."}
            </p>
            <button
              type="button"
              onClick={() =>
                currentPage > 1
                  ? setCurrentPage(1)
                  : navigate("/eleven/products/new")
              }
              className="mt-5 px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {currentPage > 1 ? "Go to first page" : "+ Add Product"}
            </button>
          </div>
        )
      )}

      {/* Pagination */}
      {!loading &&
        products.length > 0 &&
        pagination &&
        pagination.totalPages > 0 && (
          <div className="mt-4 bg-white rounded-lg border border-gray-200 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-800">{showingFrom}</span>
              –<span className="font-semibold text-gray-800">{showingTo}</span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800">
                {pagination.totalProducts}
              </span>{" "}
              products
            </p>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goTo(currentPage - 1)}
                disabled={!pagination.hasPreviousPage || loading}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>

              {pageList(pagination.currentPage, pagination.totalPages).map(
                (p, i) =>
                  p === "…" ? (
                    <span
                      key={`gap-${i}`}
                      className="px-2 text-sm text-gray-400"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goTo(p)}
                      className={`min-w-[2.25rem] px-2 py-1.5 text-sm font-medium rounded-lg border ${
                        p === pagination.currentPage
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  ),
              )}

              <button
                type="button"
                onClick={() => goTo(currentPage + 1)}
                disabled={!pagination.hasNextPage || loading}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default ElevenProducts;
